import { Trans, useLingui } from "@lingui/react/macro";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft02Icon, ArrowRight02Icon } from "@hugeicons/core-free-icons";
import { NavLink } from "react-router";
import { useAuth } from "@/auth/use-auth";
import { useUserFeatureFlags } from "@/lib/feature-flags";
import { cn } from "@/lib/utils";
import { MayflowerMark } from "./MayflowerMark";
import { NAV_GROUPS, type NavItem } from "./nav-config";
import { UserMenu } from "./UserMenu";
import { useSidebarCollapse } from "./useSidebarCollapse";

interface SidebarLinkProps {
  item: NavItem;
  collapsed: boolean;
}

function SidebarLink({ item, collapsed }: Readonly<SidebarLinkProps>) {
  const { i18n } = useLingui();
  const label = i18n._(item.label);
  return (
    <NavLink
      to={item.path}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          "flex w-full items-center gap-2.5 rounded-md text-sm font-medium transition-colors",
          "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
          collapsed ? "justify-center px-2 py-2" : "px-3 py-2",
          isActive
            ? "bg-accent text-primary"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        )
      }
    >
      <HugeiconsIcon icon={item.icon} size={16} className="shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

/**
 * Application sidebar — the only persistent chrome shown on every
 * authenticated route. Drives nav purely via react-router `NavLink` so
 * the active state stays in sync with the URL.
 */
export function Sidebar() {
  const { isAdmin } = useAuth();
  const { t, i18n } = useLingui();
  const flags = useUserFeatureFlags();
  const { collapsed, canCollapse, toggle } = useSidebarCollapse();
  const rawGroups = isAdmin ? NAV_GROUPS.admin : NAV_GROUPS.user;
  const groups = rawGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.id === "voice-auth-admin") return flags.voiceAuth;
        if (item.id === "avatars-3d") return flags.avatars3d;
        if (item.id === "avatars-liveavatar") return flags.avatarsLiveavatar;
        return true;
      }),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <aside
      className={cn(
        "flex shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm",
        "transition-[width] duration-200 ease-out min-h-0",
      )}
      style={{ width: collapsed ? 64 : 216 }}
    >
      {/* Brand row */}
      <div
        className={cn(
          "flex shrink-0 items-center border-b border-border",
          collapsed
            ? "flex-col justify-center gap-2 px-2 py-3"
            : "justify-between gap-2.5 px-3.5 py-3 pl-3.5",
        )}
      >
        {collapsed ? (
          <MayflowerMark size={24} />
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <MayflowerMark size={24} />
            <span className="text-sm font-semibold tracking-tight">
              <Trans>Voicebot</Trans>
            </span>
            {isAdmin && (
              <span className="rounded-sm bg-[color:var(--color-warning-bg)] px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider text-[color:var(--color-warning)]">
                <Trans>Admin</Trans>
              </span>
            )}
          </div>
        )}
        {canCollapse && (
          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? t`Expand sidebar` : t`Collapse sidebar`}
            title={collapsed ? t`Expand sidebar` : t`Collapse sidebar`}
            className={cn(
              "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors",
              "hover:bg-accent hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
            )}
          >
            <HugeiconsIcon
              icon={collapsed ? ArrowRight02Icon : ArrowLeft02Icon}
              size={13}
            />
          </button>
        )}
      </div>

      {/* Scrollable nav */}
      <nav
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto overflow-x-hidden",
          collapsed ? "px-2 py-3" : "px-2.5 py-3",
        )}
      >
        {groups.map((group) => (
          <div
            key={group.id}
            className="flex flex-col gap-0.5"
            data-testid={`nav-group-${group.id}`}
          >
            {!collapsed && (
              <div className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {i18n._(group.name)}
              </div>
            )}
            {collapsed && <div className="mx-2 my-1 h-px bg-border" />}
            {group.items.map((item) => (
              <SidebarLink key={item.id} item={item} collapsed={collapsed} />
            ))}
          </div>
        ))}
      </nav>

      {/* User card pinned to bottom */}
      <div
        className={cn(
          "shrink-0 border-t border-border bg-card",
          collapsed ? "px-2 py-2.5" : "px-2.5 py-2.5",
        )}
      >
        <UserMenu collapsed={collapsed} />
      </div>
    </aside>
  );
}
